import { isOneOf } from '@shared/helpers/array.helpers';
import {
  IMPORTABLE_FILE_EXTENSIONS,
  IMPORTABLE_MIME_TYPES,
} from './csv-import.constants';

export const hasImportableExtension = (filename: string): boolean => {
  const lower = filename.toLowerCase();
  return IMPORTABLE_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

/**
 * Drag events do not expose filenames, only `DataTransferItem.type` (MIME).
 * An empty MIME is common for text files and must be treated as "possibly valid";
 * final validation happens on drop via {@link hasImportableExtension}.
 */
export const isImportableMimeType = (mimeType: string): boolean =>
  mimeType === '' || isOneOf(IMPORTABLE_MIME_TYPES, mimeType);

export type DragValidity = 'idle' | 'valid' | 'invalid';

/**
 * Classifies an in-flight drag based on the items currently being dragged.
 * - `idle` when no files are in the drag
 * - `invalid` when multiple files are dragged or the MIME type is clearly not
 *   an importable file (.csv / .xlsx)
 * - `valid` otherwise (extension is validated at drop time)
 */
export const getDragValidity = (
  dataTransfer: DataTransfer | null | undefined,
): DragValidity => {
  if (!dataTransfer) return 'idle';
  if (!dataTransfer.types.includes('Files')) return 'idle';

  const fileItems = Array.from(dataTransfer.items).filter(
    (item) => item.kind === 'file',
  );

  if (fileItems.length === 0) {
    return 'valid';
  }

  if (fileItems.length > 1) return 'invalid';

  const [item] = fileItems;
  if (!item) return 'idle';
  return isImportableMimeType(item.type) ? 'valid' : 'invalid';
};
